import { useState } from "react";
import { Star, CheckCircle } from "lucide-react";
import { motion } from "framer-motion";
import { TESTIMONIALS } from "@/data/testimonials";

interface TestimonialsBassProps {
    /** Splash pages: below md, render the mobile review rail. */
    mobileRedesign?: boolean;
}

const TestimonialsBass = ({ mobileRedesign = false }: TestimonialsBassProps = {}) => {
    const [showAll, setShowAll] = useState(false);
    const testimonials = TESTIMONIALS;

    // Show a clean row of 3 by default; the rest reveal behind "Read more reviews".
    const visible = showAll ? testimonials : testimonials.slice(0, 3);

    // Mobile: centered header, then a snap rail of white review cards (3 at
    // rest; "Read N more" appends the rest to the rail).
    const hidden = testimonials.length - 3;
    const mobile = (
        <section className="md:hidden bg-[#F9FAFB] px-6 py-[72px] text-center">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#FF8B11]">Client Dossier</p>
            <h2 className="mb-8 font-playfair text-[34px] font-bold leading-[1.1] text-[#0f172a] [text-wrap:balance]">
                Trusted by <span className="text-[#c2410c]">professionals like you.</span>
            </h2>
            <div className="-mx-6 flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {visible.map((t, i) => (
                    <div
                        key={i}
                        className="w-[300px] flex-none snap-start rounded-2xl bg-white p-6 text-left shadow-[0_1px_0_rgba(15,23,42,0.06),0_16px_40px_-24px_rgba(15,23,42,0.3)]"
                    >
                        <div className="mb-3.5 flex gap-[3px]" aria-label={`${t.rating} out of 5 stars`}>
                            {[...Array(t.rating)].map((_, s) => (
                                <Star key={s} className="h-3.5 w-3.5 fill-[#FFB020] text-[#FFB020]" />
                            ))}
                        </div>
                        <p className="mb-[18px] font-playfair text-[16px] leading-[1.55] text-[#334155]">"{t.quote}"</p>
                        <div className="flex items-center gap-3">
                            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[linear-gradient(135deg,#FF8B11,#D97706)] text-[14px] font-bold text-white">
                                {t.name.charAt(0)}
                            </span>
                            <span className="text-[13px] font-bold text-[#0f172a]">{t.name}</span>
                        </div>
                    </div>
                ))}
            </div>
            <div className="mt-2 flex justify-center gap-1.5" aria-hidden="true">
                {visible.map((_, i) => (
                    <span key={i} className="h-1.5 w-1.5 rounded-full bg-[#d6d3d1]" />
                ))}
            </div>
            {hidden > 0 && (
                <button
                    type="button"
                    onClick={() => setShowAll((v) => !v)}
                    className="mt-5 h-11 rounded-md border border-[#d6d3d1] bg-white px-6 text-[11px] font-bold uppercase tracking-[0.15em] text-[#0f172a]"
                >
                    {showAll ? "Show fewer" : `Read ${hidden} more review${hidden === 1 ? "" : "s"}`}
                </button>
            )}
            <p className="mt-5 text-[12px] text-[#78716c]">
                Read more reviews on{" "}
                <a
                    href="https://maps.app.goo.gl/bhqZsvqVay1DFsib7"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="font-semibold text-[#0f172a]"
                >
                    Google
                </a>
                <a
                    href="https://www.trustpilot.com/review/cellocloset.com"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="ml-3 font-semibold text-[#0f172a]"
                >
                    Trustpilot
                </a>
            </p>
        </section>
    );

    const desktop = (
        <section className="relative bg-white py-24 sm:py-32 overflow-hidden">
            {/* Top Wavy Divider (Flows down from the Cream Portfolio section) */}
            <div className="absolute top-0 left-0 right-0 h-[30px] md:h-[50px] pointer-events-none z-20">
                <svg className="absolute top-0 w-full h-full rotate-180" viewBox="0 0 1440 120" preserveAspectRatio="none" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0,60 Q360,120 720,60 T1440,60 L1440,120 L0,120 Z" fill="#FFF7F0" />
                    <path d="M0,60 Q360,120 720,60 T1440,60" stroke="#e2d8ce" strokeWidth="1" />
                </svg>
            </div>

            {/* Minimalist Architectural Background */}
            <div className="absolute inset-0 bg-white" />
            <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:24px_24px] opacity-40" />
            
            <div className="absolute top-[20%] right-[-10%] w-[50%] h-[50%] rounded-full bg-[#c2410c]/5 blur-[120px]" />
            <div className="absolute bottom-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-[#c2410c]/5 blur-[120px]" />

            <div className="max-w-[1600px] mx-auto px-4 sm:px-8 md:px-16 lg:px-[140px] relative z-10 flex flex-col items-center">
                <div className="text-center mb-8 sm:mb-10 flex flex-col items-center">
                    <span className="text-[#c2410c] text-[9px] font-bold tracking-[0.2em] uppercase mb-2">Client Dossier</span>
                    <h2 className="text-[24px] sm:text-[32px] md:text-[36px] lg:text-[40px] font-bold font-playfair leading-[1.05] tracking-tight text-[#0a0502] max-w-2xl">
                        Trusted by <span className="text-[#c2410c]">professionals like you.</span>
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
                    {visible.map((testimonial, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1, duration: 0.6 }}
                            className="bg-white border border-slate-200 p-5 sm:p-6 rounded-md shadow-[0_15px_40px_rgba(0,0,0,0.08)] flex flex-col h-full hover:shadow-[0_25px_50px_rgba(0,0,0,0.12)] transition-shadow duration-300"
                        >
                            <div className="flex flex-col gap-3 flex-grow">
                                    <div className="flex gap-1">
                                        {[...Array(testimonial.rating)].map((_, i) => (
                                            <Star key={i} className="w-3.5 h-3.5 text-[#f59e0b] fill-[#f59e0b]" /> // High-trust gold
                                        ))}
                                    </div>
                                <blockquote className="text-slate-700 text-sm sm:text-base font-playfair leading-relaxed">
                                    "{testimonial.quote}"
                                </blockquote>
                            </div>
                            <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-slate-50 border border-slate-200 shadow-inner flex items-center justify-center text-slate-400 font-playfair text-sm">
                                    {testimonial.name.charAt(0)}
                                </div>
                                <div>
                                    <div className="text-[#0a0502] font-bold tracking-wide text-xs">{testimonial.name}</div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {testimonials.length > 3 && (
                    <button
                        onClick={() => setShowAll((v) => !v)}
                        className="mt-10 inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-300 bg-white text-[#0a0502] text-[11px] font-bold tracking-[0.15em] uppercase hover:border-[#c2410c] hover:text-[#c2410c] transition-colors"
                    >
                        {showAll ? "Show fewer" : `Read ${testimonials.length - 3} more review${testimonials.length - 3 === 1 ? "" : "s"}`}
                    </button>
                )}

                {/* Off-site review links — social-proof anchors for Google + Trustpilot. */}
                <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[12px] sm:text-[13px] text-slate-500">
                    <span className="font-medium">Read more reviews on</span>
                    <a
                        href="https://maps.app.goo.gl/bhqZsvqVay1DFsib7"
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="font-semibold text-[#0a0502] underline underline-offset-4 decoration-slate-300 hover:decoration-[#c2410c] hover:text-[#c2410c] transition-colors"
                    >
                        Google
                    </a>
                    <span className="text-slate-300" aria-hidden="true">·</span>
                    <a
                        href="https://www.trustpilot.com/review/cellocloset.com"
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="font-semibold text-[#0a0502] underline underline-offset-4 decoration-slate-300 hover:decoration-[#c2410c] hover:text-[#c2410c] transition-colors"
                    >
                        Trustpilot
                    </a>
                </div>
            </div>

            {/* Bottom Wavy Divider (Flows down to the Trustpilot section) */}
            <div className="absolute bottom-0 left-0 right-0 h-[30px] md:h-[50px] pointer-events-none z-20">
                <svg className="absolute bottom-0 w-full h-full" viewBox="0 0 1440 120" preserveAspectRatio="none" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0,60 Q360,120 720,60 T1440,60 L1440,120 L0,120 Z" fill="#FAFAFA" />
                    <path d="M0,60 Q360,120 720,60 T1440,60" stroke="#f1f5f9" strokeWidth="1" />
                </svg>
            </div>
        </section>
    );

    if (!mobileRedesign) return desktop;

    return (
        <>
            {mobile}
            <div className="hidden md:block">{desktop}</div>
        </>
    );
};

export default TestimonialsBass;
